import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';

import type { FeatureKey } from '@/api/auth';
import { canAskStudentChat } from '@/hooks/useCuratorChat';
import { IS_APP_BUILD } from '@/lib/appBrand';
import { hasTrainerSection } from '@/lib/storeTabs';
import { computeHasFeature, useAuthStore } from '@/stores/auth';

/**
 * Tariff feature gate for a student route. If the student's tariff excludes the
 * feature, render a graceful "недоступно в вашем тарифе" state instead of the
 * page (which would otherwise 403 on its API calls). Pass an array to require
 * ANY of the given features. LOCKOUT-SAFE via `computeHasFeature` — staff and
 * users whose features haven't loaded see the page.
 *
 * Отдельным файлом (02.10.2026), а не внутри `router.tsx`: гейт рендерится в проверке
 * `freeUserNoTrainers.test.ts`, а роутер тянет за собой все страницы.
 */
export function RequireFeature({
  feature,
  children,
}: {
  feature: FeatureKey | FeatureKey[];
  children: ReactNode;
}) {
  const user = useAuthStore((s) => s.user);
  const keys = Array.isArray(feature) ? feature : [feature];
  const allowed = keys.some((k) => computeHasFeature(user, k));
  if (allowed) return <>{children}</>;
  // ⚠️ Экрана «эта возможность недоступна» НЕТ НИГДЕ — молча уводим на главную.
  // Решение заказчика 31.08.2026 касалось приложения; решение владельца 14.09.2026
  // (Apple 3.1.3(f)) распространило его на сайт: там рисовался замок «Доступно
  // ученикам академии, напишите куратору», а отказ Apple 30.08 пришёл как раз со
  // снимком сайта. Экран-замок удалён, не возвращать.
  return <Navigate to="/app" replace />;
}

/**
 * Раздел тренажёров целиком: хаб `/app/trainers` и дрилл (02.10.2026, решение владельца к 1.1.1).
 *
 * ⚠️ БЕСПЛАТНИК ТРЕНАЖЁРОВ НЕ ВИДИТ ВООБЩЕ. До этого хаб стоял без гейта: вкладки у новичка не было, но ссылка из
 * урока, кнопка на пустом «Обучении» и перенаправление закрытых входов (`/app/trainers?closed=…`) приводили его к
 * заглушке «Тренажёры скоро вернутся». Платнику заглушка остаётся — как на сайте.
 *
 * Правило — `hasTrainerSection`: то же, что у вкладки и пункта меню. В приложении без учётки или без набора
 * разделов — закрыто (иначе раздел мелькнул бы до ответа сервера); на сайте — безопасно к блокировке.
 */
export function RequireTrainers({ children, app = IS_APP_BUILD }: { children: ReactNode; app?: boolean }) {
  const user = useAuthStore((s) => s.user);
  if (hasTrainerSection(user, app)) return <>{children}</>;
  return <Navigate to="/app" replace />;
}

/**
 * Чат ученика — `/app/chat` (03.10.2026, решение владельца к 1.1.1: чат в приложении платнику, бесплатнику
 * раздела нет — ни входа, ни ссылок, маршрут уводит на главную).
 *
 * ⚠️ В ПРИЛОЖЕНИИ — СТРОГО, ТЕМ ЖЕ ПРАВИЛОМ, ЧТО И ЗАПРОС (`canAskStudentChat`): только ученик и только когда
 * набор разделов пришёл и в нём есть `chat`. Обычный `RequireFeature` пускает, пока учётки или набора ещё нет, —
 * экран успел бы смонтироваться и спросить chat-service; а сервер сокет чата тарифом не закрывает вовсе. Ревьюер
 * Apple — бесплатник: в его трафике не должно быть ни одного запроса к чату.
 *
 * На сайте — как было: безопасно к блокировке.
 */
export function RequireChat({ children, app = IS_APP_BUILD }: { children: ReactNode; app?: boolean }) {
  const user = useAuthStore((s) => s.user);
  const allowed = app ? canAskStudentChat(user) : computeHasFeature(user, 'chat');
  if (allowed) return <>{children}</>;
  return <Navigate to="/app" replace />;
}
